import { useCallback, useState, useMemo } from 'react';
import {
  Panel,
  PanelContent,
  WidgetContainer,
  Grid,
  Typography,
  Select,
  MenuItem,
  S600,
  G600,
  G900,
  EditableContainer,
  TypographyWithTooltip,
  InputAdornment,
  GlobeIcon,
  useTranslations,
  useHeroSnackbar,
  TSelectProps,
  PromptDialog,
  formatSupporterEmail,
} from '@uniqkey-frontend/shared-app';
import { GetUserProfileResponse } from '@uniqkey-backend-organization-web/api-client';
import { useQueryClient } from 'react-query';
import {
  useGetCurrentUser,
  useUpdateCurrentUserProfile,
  useLanguages,
  REACT_QUERY_CURRENT_USER_KEY,
} from '../../../../hooks/reactQuery';
import { getActualUILanguageCode } from '../../../../helpers/languages';
import EditProfileModal, { IEditProfileModalSubmitResult } from '../EditProfileModal';
import useUserAPI from '../../../../hooks/useUserAPI';
import { useUser } from '../../../../contexts/UserContext';

const SELECT_SX = { width: 215 };
const SELECT_START_ADORNMENT = (
  <InputAdornment position="start">
    <GlobeIcon color={S600} />
  </InputAdornment>
);

const ProfileModule = () => {
  const { t } = useTranslations();
  const { showError, showSuccess } = useHeroSnackbar();
  const { updateCurrentUserLanguage } = useUserAPI();
  const queryClient = useQueryClient();

  const [isEditProfileModalOpen, setIsEditProfileModalOpen] = useState<boolean>(false);
  const [isChangeLanguageModalOpen, setIsChangeLanguageModalOpen] = useState<boolean>(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');
  const handleEditProfileModalOpen = useCallback(() => setIsEditProfileModalOpen(true), []);
  const handleEditProfileModalClose = useCallback(() => setIsEditProfileModalOpen(false), []);
  const handleChangeLanguageModalOpen = useCallback(() => setIsChangeLanguageModalOpen(true), []);
  const handleChangeLanguageModalClose = useCallback(() => setIsChangeLanguageModalOpen(false), []);
  const [isCurrentUserLanguageUpdating, setIsCurrentUserLanguageUpdating] = useState(false);
  const { isSupporter } = useUser();

  const {
    data: currentUser, isLoading: isCurrentUserLoading, isError,
  } = useGetCurrentUser();

  const { languages, isLoading: areLanguagesLoading } = useLanguages({
    includeOnlyAllowedLanguages: true,
  });

  const {
    mutate: mutateCurrentUserProfile,
    isLoading: isCurrentUserProfileUpdating,
  } = useUpdateCurrentUserProfile({
    useOptimisticUpdates: true,
  });

  const handleEditProfile = useCallback((value: IEditProfileModalSubmitResult) => {
    mutateCurrentUserProfile(
      value,
      {
        onError: () => showError({ text: t('common.somethingWentWrong') }),
        onSuccess: () => {
          showSuccess({ text: t('profilePage.toast.profileUpdated') });
          handleEditProfileModalClose();
        },
      },
    );
  }, [mutateCurrentUserProfile, handleEditProfileModalClose, showError, showSuccess, t]);

  const handleLanguageSelect = useCallback<NonNullable<TSelectProps['onChange']>>((event) => {
    handleChangeLanguageModalOpen();
    setSelectedLanguage(event.target.value as string);
  }, [handleChangeLanguageModalOpen]);

  const handleLanguageChange = useCallback(async () => {
    try {
      setIsCurrentUserLanguageUpdating(true);
      await updateCurrentUserLanguage({ language: selectedLanguage });
      await queryClient.invalidateQueries(REACT_QUERY_CURRENT_USER_KEY);
      // close the modal right after the current user is updated
      handleChangeLanguageModalClose();
    } catch (e) {
      showError({ text: t('common.somethingWentWrong') });
    } finally {
      setIsCurrentUserLanguageUpdating(false);
    }
  }, [
    updateCurrentUserLanguage,
    selectedLanguage,
    queryClient,
    handleChangeLanguageModalClose,
    showError,
    t,
  ]);

  const { name, email, language: preLanguage } = currentUser ?? {} as GetUserProfileResponse;

  const language = getActualUILanguageCode(preLanguage);

  const isCurrentUserUpdating = isCurrentUserProfileUpdating || isCurrentUserLanguageUpdating;

  const formattedEmail = useMemo(() => formatSupporterEmail(
    email,
    'supporter.emailSuffix',
  ), [email]);

  if (isError) {
    return null;
  }

  return (
    <Panel className="height-100-percent">
      <PanelContent p={3} pt={3.5}>
        <Grid container gap={2}>
          <WidgetContainer
            item
            withShadow
            /*
             While the user is loading, we need to have padding here so the skeleton doesn't
             take the full widget size.
             When the user is loaded - we set the proper padding in the EditableContainer below.
            */
            p={isCurrentUserLoading ? 3 : 0}
            xs={12}
            md={6}
            isLoading={isCurrentUserLoading}
          >
            <EditableContainer
              container
              item
              flexDirection="column"
              p={isCurrentUserLoading ? 0 : 3}
              rowGap={3}
              truncate={false}
              onClick={handleEditProfileModalOpen}
              disabled={isCurrentUserUpdating || isSupporter}
            >
              <Grid container flexDirection="column">
                <Typography variant="caption" color={G600} mb={1}>
                  {t('profilePage.name')}
                </Typography>
                <TypographyWithTooltip variant="body3" color={G900}>
                  {name}
                </TypographyWithTooltip>
              </Grid>
              <Grid container flexDirection="column">
                <Typography variant="caption" color={G600} mb={1}>
                  {t('profilePage.email')}
                </Typography>
                <TypographyWithTooltip variant="body3" color={G900}>
                  {formattedEmail}
                </TypographyWithTooltip>
              </Grid>
            </EditableContainer>
          </WidgetContainer>
          <WidgetContainer
            container
            item
            withShadow
            p={3}
            xs={12}
            md
            isLoading={areLanguagesLoading}
          >
            <Grid container flexDirection="column">
              <Typography variant="caption" color={G600} mb={1}>
                {t('profilePage.changeLanguage')}
              </Typography>
              <Select
                value={language}
                onChange={handleLanguageSelect}
                sx={SELECT_SX}
                startAdornment={SELECT_START_ADORNMENT}
                disabled={isCurrentUserUpdating}
              >
                {
                  languages.map((currLanguage) => (
                    <MenuItem key={currLanguage.code} value={currLanguage.code}>
                      {currLanguage.localized_name}
                    </MenuItem>
                  ))
                }
              </Select>
            </Grid>
          </WidgetContainer>
        </Grid>
      </PanelContent>
      {isEditProfileModalOpen && (
        <EditProfileModal
          isOpen={isEditProfileModalOpen}
          isLoading={isCurrentUserUpdating}
          onSubmit={handleEditProfile}
          onClose={handleEditProfileModalClose}
          currentUser={currentUser!}
        />
      )}
      {isChangeLanguageModalOpen && (
        <PromptDialog
          open={isChangeLanguageModalOpen}
          onClose={handleChangeLanguageModalClose}
          onSubmit={handleLanguageChange}
          title={t('profilePage.changeLanguageModal.title')}
          approvalButtonText={t('common.continue')}
          rejectionButtonText={t('common.cancel')}
          approvalButtonColor="primary"
          isLoading={isCurrentUserLanguageUpdating}
        />
      )}
    </Panel>
  );
};

export default ProfileModule;
